import React from "react";
import {
  AdminLimits,
  AdminBulkUpsertResult,
  AdminBulkUpsertResponse,
  AdminManagedUser,
  AdminProductOption,
} from "../../../services/platformApi";
import ProductMultiSelectDropdown from "./ProductMultiSelectDropdown";
import ProductStatusChip from "./ProductStatusChip";
import SectionLoadingOverlay from "./SectionLoadingOverlay";
import { ACTION_LABELS } from "../utils";

interface BulkResultRow extends AdminManagedUser {
  action?: string;
  addedProductTitles?: string[];
  notificationSent?: boolean;
  error?: string;
}

interface BulkUserManagementSectionProps {
  bulkEmailsInput: string;
  onBulkEmailsChange: (value: string) => void;
  normalizedBulkEmails: string[];
  availableProducts: AdminProductOption[];
  adminLimits: AdminLimits;
  productsLoading: boolean;
  adminLoadError: string;
  selectedBulkProducts: string[];
  isBulkProductDropdownOpen: boolean;
  onBulkProductDropdownOpenChange: (isOpen: boolean) => void;
  onToggleBulkProduct: (productKey: string) => void;
  bulkError: string;
  bulkCheckLoading: boolean;
  bulkCreateLoading: boolean;
  onBulkCheck: () => void;
  onBulkCreate: () => void;
  bulkCreateResponse: AdminBulkUpsertResponse | null;
  resultRows: AdminManagedUser[] | AdminBulkUpsertResult[];
}

const BulkUserManagementSection: React.FC<BulkUserManagementSectionProps> = ({
  bulkEmailsInput,
  onBulkEmailsChange,
  normalizedBulkEmails,
  availableProducts,
  adminLimits,
  productsLoading,
  adminLoadError,
  selectedBulkProducts,
  isBulkProductDropdownOpen,
  onBulkProductDropdownOpenChange,
  onToggleBulkProduct,
  bulkError,
  bulkCheckLoading,
  bulkCreateLoading,
  onBulkCheck,
  onBulkCreate,
  bulkCreateResponse,
  resultRows,
}) => {
  const isSectionLoading = productsLoading || bulkCheckLoading || bulkCreateLoading;

  return (
    <section className="admin-page__card">
      {isSectionLoading && (
        <SectionLoadingOverlay
          label={
            bulkCreateLoading
              ? "Kuriami naudotojai ir priskiriami produktai..."
              : bulkCheckLoading
                ? "Tikrinami naudotojai..."
                : "Kraunamas administravimo konfigūracijos sąrašas..."
          }
        />
      )}

      <div className="admin-page__card-header">
        <h3>1. Kurti paskyras arba pridėti produktus masiškai</h3>
        <p>
          Įrašykite el. pašto adresus, kuriems norite sukurti paskyras arba pridėti produktus. Jei el. pašto adresas jau egzistuoja, bus pridedami tik nauji produktai.
        </p>
        <p>
          Vienu kartu galima pateikti iki {adminLimits.bulkEmailLimit} el. pašto adresų arba iki{" "}
          {adminLimits.bulkEmailInputCharLimit} simbolių.
        </p>
      </div>

      <label className="admin-page__field">
        <span>El. paštai</span>
        <textarea
          value={bulkEmailsInput}
          onChange={(event) => onBulkEmailsChange(event.target.value)}
          placeholder={[
            "jonas@example.com",
            "ona@example.com",
            "vardas.pavarde@example.com",
          ].join("\n")}
          rows={8}
        />
      </label>

      <div className="admin-page__preview">
        <strong>Atpažinti el. paštai: {normalizedBulkEmails.length}</strong>
        {normalizedBulkEmails.length > 0 ? (
          <div className="admin-page__chips">
            {normalizedBulkEmails.map((email) => (
              <span key={email} className="admin-page__chip">
                {email}
              </span>
            ))}
          </div>
        ) : (
          <p>Kol kas neatpažintas nei vienas galiojantis el. pašto adresas.</p>
        )}
      </div>

      <div className="admin-page__section-block">
        <div className="admin-page__section-copy">
          <h4>Priskiriami produktai</h4>
        </div>
        {productsLoading ? (
          <p>Kraunamas produktų sąrašas...</p>
        ) : (
          <ProductMultiSelectDropdown
            availableProducts={availableProducts}
            selectedProducts={selectedBulkProducts}
            isOpen={isBulkProductDropdownOpen}
            onOpenChange={onBulkProductDropdownOpenChange}
            onToggleProduct={onToggleBulkProduct}
          />
        )}
      </div>

      {(bulkError || adminLoadError) && (
        <p className="admin-page__feedback admin-page__feedback--error">
          {bulkError || adminLoadError}
        </p>
      )}

      <div className="admin-page__actions">
        <button onClick={onBulkCheck} disabled={isSectionLoading}>
          {bulkCheckLoading ? "Tikrinama..." : "Patikrinti el. paštus"}
        </button>
        <button
          className="admin-page__button admin-page__button--primary"
          onClick={onBulkCreate}
          disabled={isSectionLoading}
        >
          {bulkCreateLoading ? "Kuriama..." : "Sukurti / priskirti produktus"}
        </button>
      </div>

      {bulkCreateResponse && (
        <div className="admin-page__summary">
          <strong>Rezultatas</strong>
          <div className="admin-page__summary-grid">
            <span>Viso: {bulkCreateResponse.summary.total}</span>
            <span>Naujų paskyrų: {bulkCreateResponse.summary.created}</span>
            <span>Atnaujinta: {bulkCreateResponse.summary.updated}</span>
            <span>Be pakeitimų: {bulkCreateResponse.summary.unchanged}</span>
            <span>Klaidų: {bulkCreateResponse.summary.failed}</span>
          </div>
        </div>
      )}

      {resultRows.length > 0 && (
        <div className="admin-page__results admin-page__results--scrollable">
          {resultRows.map((result) => {
            const row = result as BulkResultRow;

            return (
              <article key={row.normalizedEmail} className="admin-page__result-card">
                <div className="admin-page__result-top">
                  <div>
                    <strong>{row.normalizedEmail}</strong>
                    {row.name && <p>{row.name}</p>}
                  </div>
                  {row.action && (
                    <span className="admin-page__status-badge">
                      {ACTION_LABELS[row.action] || row.action}
                    </span>
                  )}
                </div>

                <div className="admin-page__result-meta">
                  <span>Auth0: {row.auth0Exists ? "yra" : "nerasta"}</span>
                  <span>Firestore: {row.firestoreExists ? "yra" : "nerasta"}</span>
                  {typeof row.emailVerified === "boolean" && (
                    <span>El. paštas patvirtintas: {row.emailVerified ? "taip" : "ne"}</span>
                  )}
                  {row.notificationSent && <span>Pranešimas išsiųstas</span>}
                </div>

                {row.productTitles && row.productTitles.length > 0 && (
                  <div>
                    <span className="admin-page__current-products">Esami produktai</span>
                    <div className="admin-page__chips">
                      {row.products?.map((productKey) => {
                        const productTitle =
                          availableProducts.find((product) => product.key === productKey)?.title || productKey;

                        return (
                          <ProductStatusChip
                            key={`${row.normalizedEmail}-${productKey}`}
                            title={productTitle}
                            isCertified={row.productCertificationStatuses?.[productKey]}
                            showCertificationStatus
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {row.addedProductTitles && row.addedProductTitles.length > 0 && (
                  <div>
                    <small>Naujai pridėta</small>
                    <div className="admin-page__chips">
                      {row.addedProductTitles.map((productTitle) => (
                        <span
                          key={`${row.normalizedEmail}-added-${productTitle}`}
                          className="admin-page__chip admin-page__chip--success"
                        >
                          {productTitle}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {row.error && (
                  <p className="admin-page__feedback admin-page__feedback--error">{row.error}</p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default BulkUserManagementSection;
